import axios from 'axios'
import React, {useCallback, useEffect, useMemo} from 'react'
import {useLocation} from 'react-router'
import {userApi} from '../../api/userApi'
import {PORTAL_TOKEN_NAME, REFRESH_TOKEN} from '../../constants/apiConstants'
import {removeCookies, setCookies} from '../../utils/cookiesUtil'

/**
 * Portal OAuth callback.
 *
 * The admin surface no longer reaches this screen: the backend's `/oauth/callback`
 * completes that exchange server-side and 302s straight to the requested page, so the
 * browser never sees an authorization code. This is the portal (workforce) flow only.
 */
export const Auth: React.FC = () => {
  const location = useLocation()
  const {mutateAsync: generateToken} = userApi.useGenerateToken()

  const query = useMemo(() => new URLSearchParams(location.search), [location])
  const code = query.get('code')

  const clearTokenCookies = (): void => {
    removeCookies(PORTAL_TOKEN_NAME)
    removeCookies(REFRESH_TOKEN)
  }

  /** Reads the ex-portal origin out of state and remembers it for later requests. */
  const resolvePostLoginUrl = useCallback((): string => {
    const stateParam = query.get('state')
    if (stateParam) {
      try {
        const state = JSON.parse(decodeURIComponent(stateParam)) as {
          migrationVersion?: number
          portalOrigin?: string
        }
        if (state.migrationVersion === 1 && state.portalOrigin) {
          const origin = new URL(state.portalOrigin)
          const isQpHost =
            /(^|\.)questionpro\.(com\.au|[a-z]{2,})$/i.test(origin.hostname) ||
            origin.origin === 'http://localhost:5173'
          if (isQpHost) {
            localStorage.setItem('wfPortalOrigin', state.portalOrigin)
          }
        }
      } catch {
        // plain string state from old portal — fall through
      }
    }
    return `${window.location.origin}/initiatives`
  }, [query])

  const generateTokenPortal = useCallback(async (): Promise<void> => {
    clearTokenCookies()
    if (!code) return

    const stateParam = query.get('state')
    let portalOrigin: string | undefined
    if (stateParam) {
      try {
        const state = JSON.parse(decodeURIComponent(stateParam)) as {
          migrationVersion?: number
          portalOrigin?: string
        }
        if (state.migrationVersion === 1 && state.portalOrigin) {
          portalOrigin = state.portalOrigin
        }
      } catch {
        // jade flow — no parseable state
      }
    }

    try {
      const tokenPayload = await generateToken({
        code: encodeURIComponent(code),
        ...(portalOrigin && {portalUrl: portalOrigin}),
      })

      if (tokenPayload?.access_token) {
        setCookies(PORTAL_TOKEN_NAME, tokenPayload.access_token)
        setCookies(REFRESH_TOKEN, tokenPayload.refresh_token)
        axios.defaults.headers.common.Authorization = `Bearer ${tokenPayload.access_token}`
        window.location.href = resolvePostLoginUrl()
      }
    } catch {
      // Network/5xx failure — nothing to recover to client-side; the callback
      // screen simply stays put with no session established.
    }
  }, [code, generateToken, resolvePostLoginUrl, query])

  useEffect(() => {
    void generateTokenPortal()
  }, [code, generateTokenPortal])

  return <div></div>
}
